<template>
  <TransitionRoot appear :show="show" as="template">
    <dialog-headless
      :static="disable"
      as="section"
      class="font-montserrat"
      @close="close"
    >
      <div class="fixed inset-0 overflow-y-auto" :style="{ zIndex }">
        <div class="min-h-screen flex justify-center items-center p-md">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0"
            enter-to="opacity-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100"
            leave-to="opacity-0"
          >
            <DialogOverlay
              :class="['fixed inset-0 bg-nightBlue-900 bg-opacity-60', classOverlay]"
            />
          </TransitionChild>

          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <div
              class="flex flex-col w-full z-20 relative"
              :class="[
                classContent
                  ? classContent
                  : 'max-w-lg py-lg px-2xl my-xl md:min-h-1/2 bg-sec-def-bg rounded-lg',
              ]"
            >
              <div v-if="!disable" class="flex justify-end items-center">
                <div
                  class="bg-grey-25 w-8 h-8 rounded-full flex items-center justify-center top-5 right-5 hover:bg-sec-hover-bg duration-300 cursor-pointer"
                  data-test="button.Close"
                  @click="close"
                >
                  <fa-icon
                    :icon="['far', 'times']"
                    class="text-secondary-txt text-5"
                  />
                </div>
              </div>

              <div>
                <slot name="image">
                  <div
                    v-if="icon.length"
                    class="bg-grey-20 w-20 h-20 rounded-full flex justify-center items-center mx-auto mb-xl mt-md"
                  >
                    <fa-icon
                      v-if="icon"
                      :icon="icon"
                      class="text-primary-txt text-4xl"
                    />
                  </div>
                </slot>
                <DialogTitle as="section">
                  <slot name="title">
                    <h3
                      class="text-6 font-medium leading-6 text-primary-txt text-center mb-md"
                      v-html="title"
                    />
                  </slot>
                </DialogTitle>

                <DialogDescription>
                  <slot name="body">
                    <section class="text-secondary-txt" v-html="description" />
                  </slot>
                </DialogDescription>
              </div>

              <slot name="actions">
                <div
                  v-if="btnTextPrimary || btnTextSecondary"
                  class="grid gap-lg pt-lg mt-auto"
                >
                  <g-button
                    v-if="btnTextPrimary"
                    full
                    :data-test="`button:${btnTextPrimary}`"
                    @click="$emit('onPrimary')"
                  >
                    {{ btnTextPrimary }}
                  </g-button>
                  <g-button
                    v-if="btnTextSecondary"
                    full
                    variant="secondary"
                    :title="btnTextSecondary"
                    :data-test="`button:${btnTextSecondary}`"
                    @click="$emit('onSecondary')"
                  >
                    {{ btnTextSecondary }}
                  </g-button>
                </div>
              </slot>
            </div>
          </TransitionChild>
        </div>
      </div>
    </dialog-headless>
  </TransitionRoot>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import type { PropType, SetupContext } from 'vue';
import {
  TransitionRoot,
  TransitionChild,
  Dialog as DialogHeadless,
  DialogOverlay,
  DialogTitle,
  DialogDescription,
} from '@headlessui/vue';

import { GButton } from '@flash-global66/g-button';
import { useZIndex } from '@flash-global66/g-hooks';

export default defineComponent({
  name: 'GModalAlert',
  components: {
    TransitionRoot,
    TransitionChild,
    DialogHeadless,
    DialogOverlay,
    DialogTitle,
    DialogDescription,
    GButton,
  },
  props: {
    title: { type: String, default: '' },
    description: { type: String, default: '' },
    classContent: { type: String, default: '' },
    classOverlay: { type: String, default: '' },
    btnTextPrimary: { type: String, default: '' },
    btnTextSecondary: { type: String, default: '' },
    icon: { type: Array as PropType<Array<string>>, default: () => [] },
    disable: { type: Boolean, default: false },
    show: { type: Boolean, default: false },
  },
  setup(props, { emit }: SetupContext) {
    const zIndex = ref(1000);
    onMounted(() => {
      const { nextZIndex } = useZIndex();
      zIndex.value = nextZIndex();
    });
    return {
      zIndex,
      close() {
        emit('close');
      },
    };
  },
});
</script>
